import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import InputWithStartAndEndAddOn from "@/app/components/ui/Inputs/InputWithStartAndEndAddOn";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import CircleSpinnerLoader from "@/app/components/ui/Reusable/LoaderComponent/CircleSpinnerLoader";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useInventoryPurchaseOrderSearchAPI } from "@/app/hooks/api_hooks/Inventory/PO/useInventoryPurchaseOrderSearchAPI";
import { usePOCreateAPI } from "@/app/hooks/api_hooks/Inventory/PO/usePOCreateAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useUtils } from "@/app/hooks/useUtils";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Tooltip } from "react-tooltip";
import OrderPurchaseFilterComponent from "../OrderPurchaseFilterComponent";
import { RiDeleteBinLine } from "react-icons/ri";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import RBACFlag from "@/app/components/core/RBACFlag";
import { toast } from "react-hot-toast";
import { FaRegCopy } from "react-icons/fa6";
import { BsThreeDotsVertical } from "react-icons/bs";
import { usePOActionsAPI } from "@/app/hooks/api_hooks/Inventory/PO/usePOActionsAPI";
import MoveToAwaitingGRNModal from "../MoveToAwaitingGRNModal";
import { GrFormFilter } from "react-icons/gr";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import CreatePOFromSheetModal from "@/app/components/routes/modals/CreatePOFromSheet";
import { useInventoryPurchaseOrderListFilterAPI } from "@/app/hooks/api_hooks/Inventory/PO/useInventoryPurchaseOrderListFilterAPI";

interface IOrderPurchaseListFilterProps {
  createdBy: string;
}

const OrderPurchaseListComponent = () => {
  const isOrderListInProgess = useLoader("isInventoryPurchaseOrderListLoadingInProgess");
  const isPOCreationInProgress = useLoader("isPOCreateInProgress");
  const userPermissions = useAppSelector((state) => state.auth.userPermissions);
  const [inputSearchWithPo, setInputSearchWithPo] = useState<string>("");
  const { getRouteKey } = useRouter();
  const { NO_PWL_RESULTS } = useImageConstants();
  // RBAC: PO_CREATE_ACTION — legacy roles: clinic_ops_bluecity_PO, central_ops, Category_Ops
  const canCreatePO = useFeatureAccess("PO_CREATE_ACTION");
  // RBAC: PO_BULK_CREATE_FROM_SHEET_ACTION — bulk-create multiple POs from a sheet
  const canCreatePOfromSheet = useFeatureAccess("PO_BULK_CREATE_FROM_SHEET_ACTION");
  const { setVisible: poDeleteModal, setModalData } = useModalActions("PO_DELETE_MODAL");
  const { setVisible: openCreatePOFromSheetModal } = useModalActions("CREATE_PO_FROM_SHEET_MODAL");
  const { convertToReadableFormat, POStatusTextColor, truncateString, veticDateFormat } = useUtils();
  const { purchaseOrderList: POList, purchaseOrderFilter } = useRouteInfo(getRouteKey("INVENTORY", "id")).routeState
    .state as IInventoryState;
  const fetchProductOrderList = useInventoryPurchaseOrderListFilterAPI();
  const fetchOrderList = useInventoryPurchaseOrderSearchAPI();
  const { execute: createPOInPreDraft } = usePOCreateAPI();
  const [openActionFor, setOpenActionFor] = useState<string | null>(null);
  const actionRefs = useRef({});
  const executePOActions = usePOActionsAPI();
  const [isAwaitingGRNModalOpen, setIsAwaitingGRNModalOpen] = useState(false);
  const [selectedPOId, setSelectedPOId] = useState<string | null>(null);
  const [_isMenuVisible, setIsMenuVisible] = useState(false);
  const formHook = useForm<IOrderPurchaseListFilterProps>({
    mode: "onChange",
    defaultValues: {
      createdBy: "",
    },
  });
  const selectedCreatedBy = formHook.watch("createdBy");

  const createdByFilterList = useMemo(() => {
    const uniqueCreatedByList = Array.from(
      new Map(
        (POList || []).map((item) => [
          item.created_by,
          {
            id: item.created_by,
            title: item.created_by,
          },
        ]),
      ).values(),
    );

    return [{ id: "", title: "All" }, ...uniqueCreatedByList];
  }, [POList]);

  const filteredPOList = useMemo(() => {
    if (!selectedCreatedBy) {
      return POList || [];
    }
    return (POList || []).filter((item) => item.created_by === selectedCreatedBy);
  }, [POList, selectedCreatedBy]);

  const handleSearchPurchaseOrders = () => {
    if (inputSearchWithPo?.length < 1) return;
    fetchOrderList(inputSearchWithPo);
  };

  const handleEnterPress = (e) => {
    if (e.key === "Enter") {
      handleSearchPurchaseOrders();
    }
  };

  const handelCreatePoId = () => {
    createPOInPreDraft().then((poid) => {
      if (poid.po_id) {
        window.open(`${getRouteKey("PURCHASE_ADD", "url")}?po_id=${poid.po_id}`, "_blank");
      }
    });
  };

  const handleCreatePOFromSheetSuccess = () => {
    if (purchaseOrderFilter) {
      fetchProductOrderList(purchaseOrderFilter);
    }
  };

  const handelPOeditAndView = (id: string) => {
    window.open(`${getRouteKey("PURCHASE_ADD", "url")}?po_id=${id}`, "_blank");
  };

  const handlePODeleteClick = (item) => {
    setModalData({
      po_id: item?.po_id,
    });
    poDeleteModal();
  };

  useEffect(() => {
    function handleClickOutside(e) {
      const openRef = actionRefs.current[openActionFor];
      if (openRef && !openRef.contains(e.target)) {
        setOpenActionFor(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openActionFor]);

  const handleThreeDotClick = (poId: string, actions: any[]) => {
    if (!actions || actions.length === 0) return;
    setOpenActionFor(openActionFor === poId ? null : poId);
  };

  const handleStatusChangeConfirm = async () => {
    if (!selectedPOId) return;
    const res = await executePOActions({
      po_id: selectedPOId,
      action_type: "move_to_awaiting_grn",
    });
    if (res?.status === true) {
      fetchOrderList(selectedPOId);
    }
  };

  return (
    userPermissions && (
      <div>
        <div className="relative bg-white rounded-md w-auto h-auto">
          <OrderPurchaseFilterComponent />
        </div>
        <div className="bg-white mt-2 px-2 pb-3 rounded-md">
          <div className="flex flex-wrap justify-between items-center gap-3 pt-1">
            <div className="w-full sm:w-1/3">
              <InputWithStartAndEndAddOn
                value={inputSearchWithPo}
                containerClassName="w-full"
                placeholder="Search by PO ID"
                onChange={(e) => setInputSearchWithPo(e.target.value)}
                onKeyDown={handleEnterPress}
                endAddon={
                  <button onClick={handleSearchPurchaseOrders} className="font-semibold text-xs cursor-pointer">
                    <MagnifyingGlassIcon className="w-4 h-4 hover:text-blue-500 hover:scale-105 transition-transform" />
                  </button>
                }
                startAddon={<span className="font-semibold text-xs cursor-pointer">PO #</span>}
              />
            </div>
            <div className="sm:flex-none flex items-center gap-3">
              {canCreatePOfromSheet && (
                <SolidButton
                  onButtonClick={() => openCreatePOFromSheetModal()}
                  className="!h-8 !px-4 !py-2 !rounded-lg !bg-white !border !border-black hover:!bg-white focus:!bg-white active:!bg-white !text-black !font-medium !text-[12px] !leading-none whitespace-nowrap"
                  size="2XL"
                >
                  <span className="flex items-center gap-1.5 whitespace-nowrap text-xs">
                    Sheet to PO
                  </span>
                </SolidButton>
              )}
              {canCreatePO && (
                <SolidButton
                  disabled={isPOCreationInProgress}
                  onButtonClick={handelCreatePoId}
                  className="!h-8 !px-4 !py-2 !rounded-lg !bg-black !border-black hover:!bg-black focus:!bg-black active:!bg-black !text-white !font-medium !text-[12px] !leading-none whitespace-nowrap"
                  size="2XL"
                >
                  {isPOCreationInProgress ? (
                    <div className="p-0 w-14">
                      <CircleSpinnerLoader h="20" w="20" />
                    </div>
                  ) : (
                    <span className="whitespace-nowrap text-xs">Create PO</span>
                  )}
                </SolidButton>
              )}
            </div>
          </div>
          <hr className="mt-2 2xl:mt-3" />
          <div className="mt-2 2xl:mt-3 flow-root">
            {isOrderListInProgess ? (
              <TableSkeleton />
            ) : filteredPOList?.length === 0 ? (
              <div className="flex justify-center items-center bg-white rounded-lg w-full">
                <NoDataFoundComponent imageURL={NO_PWL_RESULTS} message="No PO Listed" />
              </div>
            ) : (
              <div className="outer-container relative rounded-xl bg-[#F9FAFB]">
                <div className="scroll-inner !h-auto max-h-[450px] 2xl:max-h-[550px]">
                  <div className="bg-white rounded-lg">
                    <table className="w-full min-w-[1100px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                      <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal">
                        <tr>
                          <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[150px]">
                            PO Number
                          </th>
                          <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[110px]">
                            Created Date
                          </th>
                          <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[180px]">
                            Vendor Name
                          </th>
                          <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[190px]">
                            Branch Name
                          </th>
                          <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[170px]">
                            Clinic
                          </th>
                          <th className="relative overflow-visible py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[160px]">
                            <SearchableSelectMenu
                              onSelectItem={(items) => {
                                if (items) {
                                  formHook.setValue("createdBy", items.id);
                                  setIsMenuVisible(false);
                                }
                              }}
                              asButton
                              renderAsButton={
                                <button type="button" className="flex items-center gap-1 text-xs font-medium text-[#1F1F23]">
                                  Created By
                                  <GrFormFilter className="w-4 h-4" />
                                </button>
                              }
                              register={formHook.register("createdBy")}
                              selectItems={createdByFilterList}
                              containerClassName="w-52"
                              listZIndex="z-[120]"
                              fieldError={formHook.formState.errors.createdBy}
                              errorMessages={[]}
                            />
                          </th>
                          <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[150px]">
                            Status
                          </th>
                          <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[80px] sm:sticky sm:right-0 sm:z-30 bg-[#F5F5F5]">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="overflow-hidden rounded-xl w-full">
                        {filteredPOList?.map((po, id) => {
                          const isFirst = id === 0;
                          const isLast = id === filteredPOList.length - 1;
                          const isSingle = filteredPOList.length === 1;
                          const isIssueRow = ["issue_in_grn", "issue_pending_invoice_upload"].includes(po?.po_status);

                          return (
                            <tr
                              key={po?.po_id}
                              className={clsx(
                                "transition",
                                isIssueRow ? "bg-red-50" : "bg-white",
                                isSingle && "rounded-xl",
                                isFirst && !isSingle && "rounded-t-xl",
                                isLast && !isSingle && "rounded-b-xl",
                              )}
                            >
                              <td
                                className={clsx(
                                  "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                  isSingle && "rounded-tl-2xl rounded-bl-2xl",
                                  isFirst && !isSingle && "rounded-tl-2xl",
                                  isLast && !isSingle && "rounded-bl-2xl",
                                )}
                              >
                                <div className="flex gap-3 items-center">
                                  <span
                                    className="text-blue-600 cursor-pointer font-medium"
                                    onClick={() => handelPOeditAndView(po?.po_id)}
                                  >
                                    {po?.po_number}
                                  </span>
                                  <div className="relative group cursor-pointer">
                                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-black text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                                      Copy
                                    </div>
                                    <FaRegCopy
                                      className="text-gray-500 hover:text-blue-500"
                                      onClick={() => {
                                        navigator.clipboard.writeText(po?.po_number || "");
                                        toast.success(`${po?.po_number} copied`);
                                      }}
                                    />
                                  </div>
                                </div>
                              </td>

                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                {veticDateFormat(po?.created_at)}
                              </td>

                              <td
                                className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23]"
                                data-tooltip-id="user_detail_name"
                                data-tooltip-content={po?.vendor_name}
                              >
                                {truncateString(po?.vendor_name, 25)}
                              </td>

                              <td className="px-3 py-3 text-xs font-normal text-[#1F1F23] whitespace-normal break-words">
                                <span className="inline-block max-w-[180px] leading-tight">{po?.branch_name}</span>
                              </td>

                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23]">
                                {po?.clinic_name}
                              </td>

                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23]">
                                {po?.created_by}
                              </td>

                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal">
                                <div
                                  className={clsx(
                                    "flex justify-center items-center px-2 py-1.5 rounded-[20px] text-black text-xs w-auto text-center",
                                    POStatusTextColor(po?.po_status, true),
                                  )}
                                >
                                  {convertToReadableFormat(po?.po_status)}
                                </div>
                                {po?.short_closed && (
                                  <p className="text-center mt-1 text-red-500 text-xs font-medium">{po?.short_closed}</p>
                                )}
                                {po?.finance_status && (
                                  <p className="text-center mt-1 text-red-500 text-xs font-medium">{po?.finance_status}</p>
                                )}
                              </td>

                              <td
                                className={clsx(
                                  "px-3 py-3 whitespace-nowrap text-xs font-normal align-middle sm:sticky sm:right-0 sm:z-20",
                                  isIssueRow ? "bg-red-50" : "bg-white",
                                  isSingle && "rounded-tr-2xl rounded-br-2xl",
                                  isFirst && !isSingle && "rounded-tr-2xl",
                                  isLast && !isSingle && "rounded-br-2xl",
                                )}
                              >
                                {canCreatePO && (
                                  <div
                                    className="relative inline-block"
                                    ref={(el) => (actionRefs.current[po.po_id] = el)}
                                  >
                                    {po?.po_status === "awaiting_grn" ||
                                      po?.po_status === "closed" ||
                                      po?.po_status === "sent_to_finance" ? (
                                      <div
                                        className="cursor-pointer"
                                        onClick={() => {
                                          if (po?.actions?.length > 0) {
                                            handleThreeDotClick(po?.po_id, po?.actions);
                                          }
                                        }}
                                      >
                                        <BsThreeDotsVertical size={18} />
                                      </div>
                                    ) : (
                                      <div
                                        className="text-base text-red-500 cursor-pointer"
                                        onClick={() => handlePODeleteClick(po)}
                                      >
                                        <RiDeleteBinLine />
                                      </div>
                                    )}

                                    {openActionFor === po.po_id &&
                                      (() => {
                                        const isLastRow =
                                          filteredPOList[filteredPOList.length - 1]?.po_id === po.po_id;
                                        return (
                                          <div
                                            className={clsx(
                                              "absolute right-0 z-50 bg-white shadow-lg border border-gray-100 rounded-lg w-48 p-2",
                                              isLastRow ? "bottom-full mb-2" : "mt-2",
                                            )}
                                          >
                                            {po?.actions?.map((actionObj, idx) => {
                                              const key = Object.keys(actionObj)[0];
                                              const label = Object.values(actionObj)[0];
                                              return (
                                                <div
                                                  key={idx}
                                                  onClick={async () => {
                                                    setOpenActionFor(null);
                                                    if (key === "delete") {
                                                      handlePODeleteClick(po);
                                                      return;
                                                    }
                                                    if (key === "move_to_awaiting_grn") {
                                                      setSelectedPOId(po?.po_id);
                                                      setIsAwaitingGRNModalOpen(true);
                                                      return;
                                                    }
                                                  }}
                                                  className="px-3 py-2 text-xs text-[#1F1F23] hover:bg-[#F3F4F6] rounded-md cursor-pointer transition-colors"
                                                >
                                                  {key === "delete" ? (
                                                    <RBACFlag actionId="PO_DELETE_ACTION_CTA">{label}</RBACFlag>
                                                  ) : (
                                                    label
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        );
                                      })()}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <Tooltip id="user_detail_name" />
        <Tooltip id="status" />

        <MoveToAwaitingGRNModal
          open={isAwaitingGRNModalOpen}
          setOpen={setIsAwaitingGRNModalOpen}
          onConfirmClose={handleStatusChangeConfirm}
        />

        <CreatePOFromSheetModal onCreateSuccess={handleCreatePOFromSheetSuccess} />
      </div>
    )
  );
};

export default OrderPurchaseListComponent;